import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { lessonsAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
const LIMIT = 9;

const LessonSelector = ({ open, onClose, onSelectLessons, selectedLessonIds = [] }) => {
  const { t } = useTranslation(['lessons']);
  
  // Состояние для хранения списка уроков
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для пагинации
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Состояние для фильтров
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    contentType: '',
    isDraft: 'false' // По умолчанию показываем только опубликованные уроки
  });
  
  // Состояние для выбранных уроков - инициализируем из переданных ID
  const [selected, setSelected] = useState([]);

  // Обновляем выбранные уроки при изменении selectedLessonIds
  useEffect(() => {
    if (selectedLessonIds && selectedLessonIds.length > 0) {
      setSelected(selectedLessonIds);
    }
  }, [selectedLessonIds]);

  // Мемоізовані параметри для API
  const filteredParams = useMemo(() => {
    const params = {
      page,
      limit: LIMIT,
      ...filters
    };
    
    // Удаляем пустые параметры
    Object.keys(params).forEach(key => {
      if (params[key] === '') {
        delete params[key];
      }
    });
    
    return params;
  }, [page, filters]);
  
  // Функция для загрузки уроков с сервера
  const fetchLessons = useCallback(async () => {
    try {
      setLoading(true);
      
      const response = await lessonsAPI.getAll(filteredParams);
      
      if (response && response.data) {
        setLessons(response.data.data);
        setTotal(response.data.pagination.total);
      }
    } catch (err) {
      console.error('Error fetching lessons:', err);
      setError('Ошибка при загрузке уроков');
    } finally {
      setLoading(false);
    }
  }, [filteredParams]);

  // Загрузка уроков при монтировании компонента и изменении фильтров или страницы
  useEffect(() => {
    if (open) {
      fetchLessons();
    }
  }, [open, page, filters, fetchLessons]);

  // Мемоізовані обробники
  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1); // Сбрасываем страницу при изменении фильтров
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      search: '',
      type: '',
      contentType: '',
      isDraft: 'false'
    });
    setPage(1);
  }, []);

  const handleToggleSelect = useCallback((lessonId) => {
    setSelected(prev => {
      if (prev.includes(lessonId)) {
        return prev.filter(id => id !== lessonId);
      } else {
        return [...prev, lessonId];
      }
    });
  }, []);

  const handleConfirm = useCallback(() => {
    // Находим полные объекты выбранных уроков
    const selectedLessons = lessons.filter(lesson => selected.includes(lesson._id));
    onSelectLessons(selectedLessons);
    onClose();
  }, [lessons, selected, onSelectLessons, onClose]);

  // Мемоізовані значення
  const isLessonSelected = useCallback((lessonId) => {
    return selected.includes(lessonId);
  }, [selected]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Выбор уроков из библиотеки</DialogTitle>
      <DialogContent>
        {/* Фильтры */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('lessons:library.filters.searchPlaceholder')}
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('lessons:library.filters.lessonTypeLabel')}</InputLabel>
                <Select
                  name="type"
                  value={filters.type}
                  label={t('lessons:library.filters.lessonTypeLabel')}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Все типы</MenuItem>
                  <MenuItem value="text">Текст</MenuItem>
                  <MenuItem value="video">Видео</MenuItem>
                  <MenuItem value="interactive">Интерактивный</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('lessons:library.filters.contentTypeLabel')}</InputLabel>
                <Select
                  name="contentType"
                  value={filters.contentType}
                  label={t('lessons:library.filters.contentTypeLabel')}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Все типы</MenuItem>
                  <MenuItem value="standard">Стандартный</MenuItem>
                  <MenuItem value="mixed">Смешанный</MenuItem>
                  <MenuItem value="labyrinth">Лабиринт</MenuItem>
                  <MenuItem value="flippingCards">Карточки</MenuItem>
                  <MenuItem value="memoryGame">Игра на память</MenuItem>
                  <MenuItem value="tagCloud">Облако тегов</MenuItem>
                  <MenuItem value="rolePlayGame">Ролевая игра</MenuItem>
                  <MenuItem value="html">HTML</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={handleResetFilters}
                size="medium"
              >
                Сбросить фильтры
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Список уроков */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography color="error">{error}</Typography>
            <Button 
              variant="contained" 
              onClick={fetchLessons} 
              sx={{ mt: 2 }}
            >
              Повторить
            </Button>
          </Box>
        ) : lessons.length === 0 ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography>Уроки не найдены</Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {lessons.map((lesson) => {
              const isSelected = isLessonSelected(lesson._id);
              const isAlreadyInModule = selectedLessonIds.includes(lesson._id);
              
              return (
                <Grid item xs={12} sm={6} md={4} key={lesson._id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      opacity: lesson.isDraft ? 0.8 : 1,
                      border: isSelected ? '2px solid #4caf50' : isAlreadyInModule ? '2px solid #ff9800' : 'none',
                      position: 'relative'
                    }}
                  >
                    {isAlreadyInModule && !isSelected && (
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          right: 0, 
                          bgcolor: '#ff9800', 
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderBottomLeftRadius: 4
                        }}
                      >
                        <Typography variant="caption">Уже в модуле</Typography>
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div" noWrap sx={{ maxWidth: '80%' }}>
                          {lesson.title}
                        </Typography>
                        {lesson.isDraft && (
                          <Tooltip title={t('lessons:library.filters.draftTooltip')}>
                            <VisibilityOffIcon fontSize="small" color="action" />
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                        <Chip 
                          label={lesson.type === 'text' ? 'Текст' : lesson.type === 'video' ? 'Видео' : 'Интерактивный'} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                        <Chip 
                          label={
                            lesson.contentType === 'standard' ? 'Стандартный' :
                            lesson.contentType === 'mixed' ? 'Смешанный' :
                            lesson.contentType === 'labyrinth' ? 'Лабиринт' :
                            lesson.contentType === 'flippingCards' ? 'Карточки' :
                            lesson.contentType === 'memoryGame' ? 'Игра на память' :
                            lesson.contentType === 'tagCloud' ? 'Облако тегов' :
                            lesson.contentType === 'rolePlayGame' ? 'Ролевая игра' :
                            lesson.contentType === 'presentation' ? 'Презентация' :
                            lesson.contentType === 'fullHtml' ? 'Полный HTML' :
                            lesson.contentType === 'htmlBlock' ? 'HTML-блок' : 'Другой'
                          } 
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {lesson.content || 'Нет описания'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Длительность: {lesson.duration} мин.
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        fullWidth 
                        variant={isSelected ? "contained" : "outlined"}
                        color={isSelected ? "success" : "primary"}
                        startIcon={isSelected ? <CheckIcon /> : <AddIcon />}
                        onClick={() => handleToggleSelect(lesson._id)}
                      >
                        {isSelected ? 'ВЫБРАНО' : 'ВЫБРАТЬ'}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Пагинация */}
        {!loading && lessons.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={Math.ceil(total / LIMIT)} 
              page={page} 
              onChange={handlePageChange} 
              color="primary" 
            />
          </Box>
        )}
        
        {/* Информация о выбранных уроках */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Typography>Выбрано уроков: {selected.length}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">ОТМЕНА</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="primary"
          disabled={selected.length === 0}
        >
          ДОБАВИТЬ ВЫБРАННЫЕ
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LessonSelector; 